import { makeAutoObservable } from 'mobx';
import { resetAllStore } from '../../shared/stores/resetAllStore';
import { SEND_MESSAGE_WAY, SendMessageWayTypes } from '../constants/SEND_MESSAGE_WAY';
import { communicationService } from '../services/communicationService';
import { chatService } from '../services/chatService';
import {
  SendMessageFormValues,
  MessageData,
  SendMessageResourceType,
} from '../interfaces/SendMessageData';
import { NotGotMessageUser } from '../interfaces/NotGotMessageUser';
import { fileService } from '../../shared/services/fileService';

const DEFAULT_AVAILABLE_CHANNELS: SendMessageWayTypes[] = [
  SEND_MESSAGE_WAY.PUSH,
  SEND_MESSAGE_WAY.CHAT,
  SEND_MESSAGE_WAY.EMAIL,
];

type OpenCommunicationModalInfo = {
  via?: SendMessageWayTypes;
  customerIds?: number[];
  filters?: Record<string, any>;
  customersCount: number;
  resourceType: SendMessageResourceType;
  availableChannels?: SendMessageWayTypes[];
  useLegacyApi?: boolean;
};

class CommunicationModalStore {
  isModalOpen: boolean = false;
  customerIds: number[] = [];
  via: SendMessageWayTypes = 'PUSH';
  filters: Record<string, any> = {};
  customersCount: number | null = null;
  resourceType: SendMessageResourceType | null = null;
  isSendingMessage: boolean = false;
  isSentSuccess: boolean = false;
  isSentWithError: boolean = false;
  notGotMessageUsers: NotGotMessageUser[] = [];
  message?: SendMessageFormValues | null = null;
  availableChannels: SendMessageWayTypes[] = DEFAULT_AVAILABLE_CHANNELS;
  // Some user types (e.g. supplement, active-calendar) are not supported by the new API
  // and require the legacy endpoint (api/coach/message/users)
  // Need to move to the new API completely when backend will add support for all resource types
  useLegacyApi: boolean = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  closeCommunicationModal() {
    this.isModalOpen = false;
    this.via = 'PUSH';
    this.customerIds = [];
    this.filters = {};
    this.customersCount = null;
    this.resourceType = null;
    this.isSendingMessage = false;
    this.isSentSuccess = false;
    this.isSentWithError = false;
    this.notGotMessageUsers = [];
    this.message = null;
    this.availableChannels = DEFAULT_AVAILABLE_CHANNELS;
    this.useLegacyApi = false;
  }

  openCommunicationModal({
    via,
    customerIds = [],
    filters = {},
    customersCount,
    resourceType,
    availableChannels = DEFAULT_AVAILABLE_CHANNELS,
    useLegacyApi = false,
  }: OpenCommunicationModalInfo) {
    this.setAvailableChannels(availableChannels);
    this.setVia(via || this.getDefaultVia(availableChannels));
    this.setCustomerIds(customerIds);
    this.setFilters(filters);
    this.setIsModalOpen(true);
    this.setCustomersCount(customersCount);
    this.setResourceType(resourceType);
    this.setUseLegacyApi(useLegacyApi);
  }

  private getDefaultVia(availableChannels: SendMessageWayTypes[]): SendMessageWayTypes {
    if (availableChannels.includes(SEND_MESSAGE_WAY.PUSH)) {
      return SEND_MESSAGE_WAY.PUSH;
    }

    return availableChannels?.[0] || SEND_MESSAGE_WAY.PUSH;
  }

  async sendMessage(
    message: SendMessageFormValues,
    files?: File[],
    sendVia?: SendMessageWayTypes,
  ) {
    this.handleStartSending();
    this.setMessage(message);

    try {
      const messageData: MessageData = await this.getMessageData(message, files, sendVia);

      const { sent: notGotMessageUsers } = this.useLegacyApi
        ? await this.sendViaLegacyApi(messageData)
        : await this.sendViaNewApi(messageData);

      if (notGotMessageUsers.length) {
        this.handleSentWithError(notGotMessageUsers);
      } else {
        this.handleSentSuccess();
      }
    } catch {
      // show general error modal
      this.closeCommunicationModal();
    }
  }

  private async sendViaNewApi(messageData: MessageData) {
    return communicationService.sendMessage({
      messageData,
      filters: this.filters,
      resourceType: this.resourceType!,
    });
  }

  // TODO: Remove this method once the backend supports all resource types in the new API
  //  (/api/data-filters/communication/message) — then we can use sendViaNewApi exclusively.
  private async sendViaLegacyApi(messageData: MessageData) {
    const { via: sendVia, message, title, attachmentFileIds } = messageData;
    // Legacy API (/api/coach/message/users) does not support CHAT channel,
    // so we fall back to the dedicated chat endpoint for legacy user types.
    if (sendVia === SEND_MESSAGE_WAY.CHAT) {
      await chatService.sendMessage(message, this.customerIds, attachmentFileIds ?? []);
      return { sent: [] as NotGotMessageUser[] };
    }

    return communicationService.sendMessageViaLegacyApi({
      via: sendVia.toLowerCase(),
      userIds: this.customerIds,
      title,
      message,
    });
  }

  private async getMessageData(
    message: SendMessageFormValues,
    files?: File[],
    sendVia?: SendMessageWayTypes,
  ): Promise<MessageData> {
    let attachmentFileIds;

    if (files?.length) {
      const uploadFilesResults = await fileService.uploadFiles('CHAT_ATTACHMENT', files);
      attachmentFileIds = uploadFilesResults.map((file) => file.id);
    }

    return {
      ...message,
      via: sendVia ?? this.via,
      userIds: this.customerIds,
      attachmentFileIds,
    };
  }

  resentViaEmail() {
    this.setIsSentWithError(false);

    const title = this.message?.title ?? 'SWYMO Default Subject';

    this.sendMessage({ ...this.message!, title }, undefined, 'EMAIL');
  }

  setVia(via: SendMessageWayTypes) {
    this.via = via;
  }

  handleStartSending() {
    this.setIsModalOpen(false);
    this.setIsSendingMessage(true);
  }

  handleSentSuccess() {
    this.setIsSendingMessage(false);
    this.setIsSentSuccess(true);
  }

  handleSentWithError(notGotMessageUsers: NotGotMessageUser[]) {
    this.setNotGotMessageUsers(notGotMessageUsers);
    this.setIsSendingMessage(false);
    this.setIsSentWithError(true);
  }

  private setCustomerIds(customerIds: number[]) {
    this.customerIds = customerIds;
  }

  private setIsModalOpen(isModalOpen: boolean) {
    this.isModalOpen = isModalOpen;
  }

  private setFilters(filters: Record<string, any>) {
    this.filters = filters;
  }

  private setCustomersCount(customersCount: number) {
    this.customersCount = customersCount;
  }

  private setResourceType(resourceType: SendMessageResourceType) {
    this.resourceType = resourceType;
  }

  private setIsSendingMessage(isSendingMessage: boolean) {
    this.isSendingMessage = isSendingMessage;
  }

  private setIsSentSuccess(isSentSuccess: boolean) {
    this.isSentSuccess = isSentSuccess;
  }

  private setIsSentWithError(isSentWithError: boolean) {
    this.isSentWithError = isSentWithError;
  }

  private setNotGotMessageUsers(notGotMessageUsers: NotGotMessageUser[]) {
    this.notGotMessageUsers = notGotMessageUsers;
  }

  private setMessage(message: SendMessageFormValues) {
    this.message = message;
  }

  private setAvailableChannels(availableChannels: SendMessageWayTypes[]) {
    this.availableChannels = availableChannels;
  }

  private setUseLegacyApi(useLegacyApi: boolean) {
    this.useLegacyApi = useLegacyApi;
  }
}

const communicationModalStore = new CommunicationModalStore();

resetAllStore.addResetMethod(communicationModalStore.closeCommunicationModal);

export { communicationModalStore, CommunicationModalStore };
